import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { getTokens, signOut } from '../auth/AuthState';
import { redirect } from 'react-router-dom';

const app = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 30000,
  headers: {
    Accept: '*/*',
    'Content-Type': 'application/json',
  },
});

const URLS_WITHOUT_TENANCY_ID = [
  'createClinician',
  'getAmtItems',
  'getClinicianProfileBasic',
  'getUserAccess',
  'markNotificationRead',
  'updateClinicianProfile',
  'updateUserFeatures',
  'getAllClinics',
  'external-connections/withings/callback',
];

function JsObjectToClass<T>(data: any, classInstance: { new (): T }) {
  const instance = new classInstance();
  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      (instance as unknown as Record<string, unknown>)[key] =
        data[key as keyof AxiosResponse];
    }
  }
  return instance;
}
function extractResponse<T>(
  response: AxiosResponse,
  classInstance: { new (): T },
): T {
  return JsObjectToClass(response.data, classInstance);
}

function handleErrorResponse(error: unknown) {
  if (
    axios.isCancel(error) ||
    (error instanceof Error && error.name === 'AbortError')
  ) {
    return null;
  }

  if (axios.isAxiosError(error)) {
    const message = String(error.message || '').toLowerCase();
    if (
      error.code === 'ECONNABORTED' ||
      error.code === 'ETIMEDOUT' ||
      message.includes('timeout')
    ) {
      return new Error('Timeout');
    }
  }

  if (axios.isAxiosError(error) && error.response) {
    const errorData = error.response.data as Record<string, any>;

    if (
      error.response.status < 500 &&
      ('message' in errorData || 'messageText' in errorData)
    ) {
      const message = errorData.message || errorData.messageText;
      return new Error(message);
    }
  }
  if (axios.isAxiosError(error)) {
    if (error.code === 'ERR_NETWORK') {
      return new Error('Network Error');
    }
  }

  console.error('API error:', error);

  return null;
}

export async function post<T = any>(
  url: string,
  body: any,
  headers: object,
  classInstance: { new (): T },
  signal?: AbortSignal,
): Promise<T>;
export async function post<T = any>(
  url: string,
  body?: any,
  headers?: object,
  classInstance?: undefined,
  signal?: AbortSignal,
): Promise<AxiosResponse<T>>;
export async function post<T = any>(
  url: string,
  body: any = {},
  headers: object = {},
  classInstance?: { new (): T },
  signal?: AbortSignal,
): Promise<T | AxiosResponse<T>> {
  const { id: token } = await getTokens();
  const FULL_IGNORE_TENANCY_ID_LIST = [
    ...URLS_WITHOUT_TENANCY_ID,
    ...URLS_WITHOUT_TENANCY_ID.map((url) => '/' + url),
  ];
  if (!FULL_IGNORE_TENANCY_ID_LIST.includes(url)) {
    const clinicianProfileState = JSON.parse(
      localStorage.getItem('clinicianProfileState') || '{}',
    );
    body.tenancyId = clinicianProfileState?.selectedTenancy;
  }
  if (!token) {
    signOut();
    redirect('/login');
    throw new Error('Not authenticated');
  }

  if (body.access_token && !isUUID(body.access_token)) {
    throw new Error('No Access to patient profile: Invalid access token');
  }

  // Apply custom timeout for getPatientList (60s), default is 30s for other APIs
  const timeout =
    url === 'getPatientList' || url === '/getPatientList' ? 60000 : undefined;

  const config: AxiosRequestConfig = {
    method: 'post',
    url,
    headers: { ...headers, Authorization: `Bearer ${token}` },
    responseType: 'json',
    data: body,
    signal,
    ...(timeout && { timeout }),
  };
  const response: any = await app({ ...config } as AxiosRequestConfig).catch(
    handleErrorResponse,
  );

  if (!response) {
    throw new Error('Request cancelled');
  }
  if (response instanceof Error) {
    throw response;
  }

  const guardedResponse = guardResponse(response);

  if (classInstance) {
    return extractResponse<T>(guardedResponse, classInstance);
  }
  if (guardedResponse) {
    return guardedResponse as AxiosResponse<T>;
  }
  throw new Error('Request failed');
}

export default app;

const isUUID = (str: string) => {
  const uuidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return uuidRegex.test(str);
};

const guardResponse = (response: any) => {
  // Ensure response structure exists
  if (!response || !response.data) return response;
  const message: string = String(response.data.message || '');
  const dataIsNull: boolean =
    response.data.data === null && response.data.message !== 'Success';

  if (dataIsNull) {
    const lower = message.toLowerCase();
    const isUserNoAccess =
      lower.includes('user has no permissions') ||
      lower.includes('user missing permissions') ||
      lower.includes('does not belong to tenancy');

    // Explicit user-level no access -> dispatch user no access
    if (isUserNoAccess) {
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('global-no-access-user', {
              detail: {
                message,
                httpStatus: response.status ?? 200,
                dataNull: true,
              },
            }),
          );
        }
      } catch {}
      // Throw to stop normal flow and allow ErrorBoundary to take over
      throw new Error(message || 'User access forbidden');
    }

    // Patient details route -> dispatch patient no access
    const onPatientDetails =
      typeof window !== 'undefined' &&
      typeof window.location !== 'undefined' &&
      String(window.location.pathname).includes('/patientDetails');

    if (onPatientDetails) {
      try {
        window.dispatchEvent(
          new CustomEvent('global-no-access-patient', {
            detail: {
              message,
              httpStatus: response.status ?? 200,
              dataNull: true,
            },
          }),
        );
      } catch {}
      throw new Error(message || 'No Access to patient profile');
    }

    // Fallback to generic app error
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('global-app-error', { detail: { message } }),
        );
      }
    } catch {}
    throw new Error(message || 'Error');
  }

  return response;
};
